/**
 * TERRA:APIS custom: sticky add-to-cart for mobile product pages.
 *
 * Strategy: observe the original Add-to-cart button. When it scrolls out of
 * the viewport, slide the sticky bar in. When it scrolls back in, slide out.
 * Clicking the sticky button forwards to the original button click so the
 * existing product form, variant selector, validation, and cart drawer all
 * keep working unchanged.
 */
class TerraApisStickyAtc extends HTMLElement {
  connectedCallback() {
    this.originalButton =
      document.querySelector('product-form [name="add"]') ||
      document.querySelector('[name="add"]');
    this.priceEl = this.querySelector('[data-price]');
    this.stickyBtn = this.querySelector('[data-trigger-add-to-cart]');

    if (!this.originalButton) {
      this.style.display = "none";
      return;
    }

    document.body.classList.add("terraapis-sticky-atc-active");

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries[0].isIntersecting;
        // Sticky shows when original button is OUT of viewport
        this.dataset.visible = visible ? "false" : "true";
        this.setAttribute("aria-hidden", visible ? "true" : "false");
      },
      { threshold: 0 }
    );
    this.observer.observe(this.originalButton);

    // Forward clicks to the original button
    this.stickyBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      if (this.originalButton.disabled) return;
      this.originalButton.click();
    });

    // Mirror disabled state of original button
    this.disabledObserver = new MutationObserver(() => {
      const disabled =
        this.originalButton.disabled ||
        this.originalButton.getAttribute("aria-disabled") === "true";
      if (this.stickyBtn) {
        this.stickyBtn.disabled = disabled;
        this.stickyBtn.setAttribute("aria-disabled", String(disabled));
      }
    });
    this.disabledObserver.observe(this.originalButton, {
      attributes: true,
      attributeFilter: ["disabled", "aria-disabled"],
    });

    // Mirror price + variant changes from product form
    document.addEventListener("change", (e) => {
      if (e.target.closest("product-form, .product-form")) {
        // Defer slightly so Dawn's variant-radios.js updates the DOM first
        setTimeout(() => this.refreshPrice(), 50);
      }
    });
  }

  refreshPrice() {
    if (!this.priceEl) return;
    const formPrice =
      document.querySelector("price-element .price__regular .price-item--regular") ||
      document.querySelector(".price-item--regular") ||
      document.querySelector(".product__info-wrapper .price__regular");
    if (formPrice && formPrice.textContent.trim()) {
      this.priceEl.textContent = formPrice.textContent.trim();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.disabledObserver?.disconnect();
    document.body.classList.remove("terraapis-sticky-atc-active");
  }
}

if (!customElements.get("terraapis-sticky-atc")) {
  customElements.define("terraapis-sticky-atc", TerraApisStickyAtc);
}
